// @flow
import React, { type Node, useCallback, useEffect, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { forEach } from 'ramda';

import PlainModal, {
  PlainModalHeader,
  PlainModalContent,
  PlainModalContainer,
  PlainModalFooter,
  useModalState,
  type HandleModalDisplayed,
} from 'app/common/ui-next/plain-modal';

import SimpleDate from 'app/common/classes/simple-date';
import { useCurrencySign } from 'app/common/components-next/currency';
import Person from 'app/common/classes/person';

import FoodItems from './FoodItems';
import OrderForm from './OrderForm';
import FoodCategoryNav from './FoodCategoryNav';
import useOrderCreate from './useOrderCreate';
import OrderFooter from './OrderFooter';
import useComplexesRequest from '../useComplexesRequest';
import useComplexesFreeRequest from '../useComplexesFreeRequest';
import OrderFormFooter from '../../../../commonComponents/OrderFormFooter';
import OrderFormHeader from '../../../../commonComponents/OrderFormHeader';
import useAvailable from '../../useAvailable';
import useOrderSalesSubmit from '../useOrderSalesSubmit';
import { useCustomerSettings } from '../../../../CafeteriaSettings/dataProviders/customer/hooks';
import type { GroupType } from '../../../../dataTypes/orderRequests/groupTypes';

import styles from './OrderModal.module.scss';;


type Props = {
  children: (HandleModalDisplayed) => Node,
  header: string,
  customer: Person,
  date: SimpleDate,
  groupType: GroupType | null,
  orderRequestId: string,
  changeId: string,
  onlySingleComplex?: boolean,
  successMessage: string,
  onOrderSubmitSuccess: () => Promise<void> | void,
  errorProcessing: (error: Error) => string,
  requestOrderInitialState?: () => Promise<Array<{ id: string, amount: number }> | null>
}

const OrderModal = ({
  children,
  header,
  customer,
  date,
  groupType,
  orderRequestId,
  changeId,
  onlySingleComplex,
  successMessage,
  onOrderSubmitSuccess,
  errorProcessing,
  requestOrderInitialState,
}: Props): Node => {
  const { t } = useTranslation('cafeteria');
  const currency = useCurrencySign();
  const { id: customerId } = customer;
  const customerID = String(customerId);

  const [loading, setLoading] = useState(true);
  const [modalOpened, openModal, closeModal] = useModalState();

  const [available, requestAvailable] = useAvailable(customerId, date);
  const {
    settings: customerSettings,
    request: requestCustomerSettings,
  } = useCustomerSettings({ autoload: false, params: { customerID } });

  const [orderCreateState, orderCreateActions] = useOrderCreate(available, groupType, onlySingleComplex);

  const {
    complexes,
    orderItems,
    orderSettings,
  } = orderCreateState;

  const {
    setComplexes,
    addToOrder,
    initToOrder,
    incrementAmount,
    decrementAmount,
    removeFromOrder,
    resetOrder,
  } = orderCreateActions;

  const { totalInOrder, balanceAvailable } = orderSettings;

  const [requestFreeComplexes] = useComplexesFreeRequest({
    customerId,
    date,
    orderRequestId,
    onStartRequest: setComplexes,
    onFinishRequest: setComplexes,
  });

  const [requestComplexes, loadingComplexes] = useComplexesRequest({
    customerId,
    date,
    orderRequestId,
    onSuccess: requestFreeComplexes,
    onFailure: closeModal,
  });

  const orderInit = useCallback(async (): Promise<void> => {
    if (requestOrderInitialState) {
      const order = await requestOrderInitialState();
      if (!order) {
        closeModal();
        return;
      }
      forEach(({ id, amount }) => {
        initToOrder(id, amount);
      }, order);
    }
    setLoading(false);
  }, [requestOrderInitialState, initToOrder, closeModal]);

  const updateModalVisibility = useCallback((visible: boolean) => {
    if (visible) {
      requestAvailable();
      requestCustomerSettings();
      requestComplexes();
      return;
    }
    resetOrder();
    closeModal();
    setLoading(true);
  }, [requestAvailable, requestComplexes, resetOrder, closeModal, requestCustomerSettings]);

  const [orderSubmit, sending] = useOrderSalesSubmit({
    orderId: orderRequestId,
    changeId,
    customerId,
    orderItemsMap: orderItems,
    onSuccess: onOrderSubmitSuccess,
    successMessage,
    errorProcessing,
  });

  // после полной загрузки комплектов инициализируется заказ
  useEffect(() => {
    if (!loadingComplexes) {
      orderInit();
    }
  }, [loadingComplexes]); // eslint-disable-line react-hooks/exhaustive-deps

  // destructor
  useEffect(() => () => {
    resetOrder();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Fragment>
      {children(openModal)}

      <PlainModal
        className={styles.root}
        show={modalOpened}
        onDisplayStatusChange={updateModalVisibility}
      >
        <PlainModalHeader>
          {header}
        </PlainModalHeader>

        <PlainModalContainer className={styles.container}>
          <PlainModalContent className={styles.topBar} unscrollable paddingFree>
            <FoodCategoryNav className={styles.topBarNavigation} />
          </PlainModalContent>
          <PlainModalContent className={styles.topBar} unscrollable paddingFree>
            <OrderFormHeader
              className={styles.topBarBalance}
              balance={customerSettings.balance}
              balanceAvailable={balanceAvailable}
            />
          </PlainModalContent>
        </PlainModalContainer>

        <PlainModalContainer className={styles.container}>
          <PlainModalContent>
            <FoodItems
              checkedMap={orderItems}
              complexes={complexes}
              onCheckItem={addToOrder}
              loading={loadingComplexes}
              disabled={sending}
            />
          </PlainModalContent>
          <PlainModalContent unscrollable paddingFree>
            <OrderForm
              orderItems={orderItems}
              currency={currency}
              pending={loading}
              disabled={sending}
              onCountIncrement={incrementAmount}
              onCountDecrement={decrementAmount}
              onRemove={removeFromOrder}
            />
          </PlainModalContent>
        </PlainModalContainer>

        <PlainModalFooter>
          {setDisplayedModal => (
            <OrderFooter
              cancelButtonText={t('order_request_sales_put_on_food_close', 'Закрыть')}
              onCancel={setDisplayedModal}
            >
              <OrderFormFooter
                className={styles.footerOrderControls}
                disabled={orderItems.size === 0 || balanceAvailable < 0}
                orderTotal={totalInOrder}
                sending={sending}
                onOrderClear={resetOrder}
                onOrderSubmit={orderSubmit}
              />
            </OrderFooter>
          )}
        </PlainModalFooter>
      </PlainModal>
    </Fragment>
  );
};

export default OrderModal;
